import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';
import moment, { Moment } from 'moment';
import { getDatepickerLocale } from '@/helpers/LocaleHelper';
import { SpreadsheetEditor } from '@/modules/Logistics/components/index';
import type {
    SpreadsheetData,
    MontageListFormData,
    MontageListPrintData,
    VehicleDropdownOption,
    MontageTeamDropdownOption,
} from '@/modules/Logistics/interfaces/LogisticsInterfaces';


export default /*#__PURE__*/_defineComponent({
  __name: 'MontageListPrint',
  props: {
    printData: null,
    montageTeams: null,
    vehicles: null
  },
  emits: ["update:formField", "spreadsheet-change"],
  setup(__props: any, { expose, emit }: { emit: ({
    (
        e: 'update:formField',
        field: keyof MontageListFormData,
        value: MontageListFormData[keyof MontageListFormData],
    ): void;
    (e: 'spreadsheet-change', data: SpreadsheetData | null): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    printData: MontageListPrintData;
    montageTeams: MontageTeamDropdownOption[];
    vehicles: VehicleDropdownOption[];
};





expose({
    print,
});
const spreadsheetEditorRef = ref<InstanceType<typeof SpreadsheetEditor> | null>(null);

const formData = computed(() => props.printData.formData);

const locale = computed(() => getDatepickerLocale());

const montageDateMoment = computed({
    get: () => {
        if (!formData.value?.date) return null;
        const parsed = moment(formData.value.date, ['YYYY-MM-DD', 'DD.MM.YYYY', moment.ISO_8601]);
        return parsed.isValid() ? parsed : null;
    },
    set: (value: Moment | null) => {
        if (value && value.isValid()) {
            emit('update:formField', 'date', value.format('DD.MM.YYYY'));
        } else {
            emit('update:formField', 'date', '');
        }
    },
});

function getInstallerName(team: MontageTeamDropdownOption): string {
    return `${team.name || ''} ${team.lastName || ''}`.trim();
}

const selectedVehicle = computed(() => {
    const currentValue = formData.value?.vehicle;
    if (!currentValue) return undefined;
    const match = props.vehicles.find((v) => v.registration === currentValue);
    return match ? match.registration : currentValue;
});

const selectedInstaller = computed(() => {
    const currentValue = formData.value?.installer;
    if (!currentValue) return undefined;
    const match = props.montageTeams.find((t) => getInstallerName(t) === currentValue);
    return match ? getInstallerName(match) : currentValue;
});

function filterOption(input: string, option: any): boolean {
    const label = option.componentOptions?.children?.[0]?.text || '';
    return label.toLowerCase().includes(input.toLowerCase());
}

function handleVehicleChange(value: string | undefined) {
    emit('update:formField', 'vehicle', value || '');
}

function handleInstallerChange(value: string | undefined) {
    emit('update:formField', 'installer', value || '');
}

function handleOtherInstallersChange(value: string) {
    emit('update:formField', 'otherInstallers', value || '');
}

function handlePreparedByChange(value: string) {
    emit('update:formField', 'preparedBy', value || '');
}

const tableHeaders = computed(() => {
    const data = props.printData.spreadsheetData;
    if (!data?.rows?.[0]?.cells) return [];

    const headerRow = data.rows[0].cells;
    const headers: string[] = [];
    const cellKeys = Object.keys(headerRow)
        .map(Number)
        .sort((a, b) => a - b);

    for (const key of cellKeys) {
        headers.push(headerRow[key]?.text || '');
    }

    return headers;
});

const tableRows = computed(() => {
    const data = props.printData.spreadsheetData;
    if (!data?.rows) return [];

    const rows: string[][] = [];
    const rowKeys = Object.keys(data.rows)
        .map(Number)
        .sort((a, b) => a - b);
    const colCount = tableHeaders.value.length;

    for (const rowKey of rowKeys) {
        if (rowKey === 0) continue;

        const rowData = data.rows[rowKey];
        if (!rowData?.cells) continue;

        const row: string[] = [];
        for (let col = 0; col < colCount; col++) {
            row.push(rowData.cells[col]?.text || '');
        }
        rows.push(row);
    }

    return rows;
});

function handleCellChange(change: { text: string; row: number; col: number }) {
    console.log('MontageListPrint cell changed:', change);
}

function handleDataChange(data: SpreadsheetData | null) {
    emit('spreadsheet-change', data);
}

function print() {
    window.print();
}

return { __sfc: true,props, emit, spreadsheetEditorRef, formData, locale, montageDateMoment, getInstallerName, selectedVehicle, selectedInstaller, filterOption, handleVehicleChange, handleInstallerChange, handleOtherInstallersChange, handlePreparedByChange, tableHeaders, tableRows, handleCellChange, handleDataChange, print, SpreadsheetEditor }
}

})